import React, { useEffect, useState, useCallback } from "react";
import NavHeader from "../components/nav/NavHeader";
import Sidenav from "../components/nav/Sidemenu";
import { Link, useParams, useNavigate } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";
import NodataAvailable from "../assets/img/NoDataAvailable.jpg";
import {
  FaAngleDown,
  FaAngleUp,
  FaThLarge,
  FaLock,
  FaArrowLeft,
  FaClock,
  FaEye,
} from "react-icons/fa";
import UserInfo from "../components/auth/Getusers";
import LandingKPIReport from "../components/pageLayouts/LandingKPIReport";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import RankedTable from "../components/layout/RankedTable";
import DepartmentCard from "../components/layout/DepartmentCard";
import PowerBIIcon from "../components/icons/PowerBIIcon";
import { useAsync } from "../components/hooks/useAsync";
import { getreportsList, getReportsByGroupId } from "../components/services/FetchAPI";
import { getToken } from "../components/auth/authConfig";

import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import Login from "./Login";

const Overview = () => {
  const { groupid } = useParams();
  const navigate = useNavigate();
  const [iconsD, setIconsD] = useState(true);
  const [iconsR, setIconsR] = useState(true);
  const [Tabvalue, setTabValue] = useState(0);
  const [reportView, setReportView] = useState("grid");
  const [passtoken, setPasstoken] = useState(null);

  // Get token on component mount
  useEffect(() => {
    const initToken = async () => {
      const token = await getToken();
      setPasstoken(token);
    };
    initToken();
  }, []);

  // Memoized callback to fetch reports
  const fetchReports = useCallback(() => {
    if (!passtoken) {
      return Promise.resolve(null); // Wait for token to be available
    }
    // When a groupid is present in the URL, scope reports to that persona/user_group
    return groupid
      ? getReportsByGroupId(groupid, passtoken)
      : getreportsList(passtoken);
  }, [passtoken, groupid]);

  const {
    loading,
    errorData,
    value: reportListData,
  } = useAsync(fetchReports, [passtoken, groupid]);

  useEffect(() => {
    document.title = "Home";
  }, [passtoken]);

  const toggleDept = () => {
    setIconsD((prev) => !prev);
  };

  const toggleReport = () => {
    setIconsR((prev) => !prev);
  };

  const homeMainStyle = {
    height: "auto",
    minHeight: "calc(100vh - 86px)",
    overflowY: "visible",
  };

  // Use reportListData
  const getClientsData = reportListData?.data;
  const isAccessDenied = reportListData?.message === "User is not assigned to this group";
  const reports             = getClientsData?.reports             || [];
  const assignedPersonas    = getClientsData?.assigned_personas   || [];
  const userGroupSet        = getClientsData?.userGroupSet
    || (getClientsData?.userGroupId ? { [getClientsData.userGroupId]: getClientsData.userGroupName } : {});
  const userInfo            = getClientsData?.userInfo            || {};
  const visuals             = getClientsData?.visuals             || [];
  const userGroupName       = getClientsData?.userGroupName       || "";

  // Store user group and RL type in localStorage
  if (getClientsData?.userGroupId) {
    localStorage.setItem("userGroup", getClientsData?.userGroupId);
  }
  if (getClientsData?.defaultRlType || getClientsData?.rlType) {
    localStorage.setItem("RLTYPE", getClientsData?.defaultRlType || getClientsData?.rlType);
  }
  if (getClientsData?.userGroupSet) {
    localStorage.setItem("userGroupSet", JSON.stringify(getClientsData.userGroupSet));
  }
  if (userInfo?.userType) {
    localStorage.setItem("userType", userInfo.userType);
  }

  return (
    <div>
      <AuthenticatedTemplate>
        <NavHeader
          rlTypeData={getClientsData?.rlType}
          defaulRLData={getClientsData?.defaultRlType}
        />
        <Sidenav />
        {getClientsData !== null && (
          <div className="relative px-4 md:px-8 pb-16" id="main" style={homeMainStyle}>
            <div
              className="pointer-events-none absolute inset-0 opacity-70"
              aria-hidden="true"
            />
            {loading && (
              <div className="relative z-10">
                {/* UserInfo bar */}
                <div className="flex items-center gap-3 py-2">
                  <Skeleton variant="circular" width={36} height={36} />
                  <Skeleton variant="text" width={180} height={24} />
                </div>

                {/* Active Workspace / KPI section */}
                <section className="mt-2 rounded-2xl border border-slate-200/80 bg-white/90 p-4 md:p-6 shadow-sm">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <Skeleton variant="text" width={120} height={14} />
                      <Skeleton variant="text" width={260} height={28} />
                    </div>
                    <Skeleton variant="rounded" width={100} height={36} />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-2">
                    {[1, 2].map((i) => (
                      <Skeleton key={i} variant="rounded" height={350} />
                    ))}
                  </div>
                </section>

                {/* Persona tabs + report grid section */}
                <section className="relative z-10 mt-5 overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-sm">
                  <div className="flex items-center gap-2 border-b border-slate-100 px-4 pt-3 pb-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} variant="rounded" width={90} height={30} />
                    ))}
                  </div>
                  <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:p-6 xl:grid-cols-3">
                    {[1, 2].map((i) => (
                      <ReportCardSkeleton key={i} />
                    ))}
                  </div>
                </section>
              </div>
            )}

            {!loading && isAccessDenied && (
              <div className="relative z-10 flex items-center justify-center py-16">
                <div className="w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-8 text-center shadow-sm">
                  <div
                    className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
                    style={{ background: "color-mix(in srgb, var(--app-primary-color, #A70E13) 12%, white)" }}
                  >
                    <FaLock className="text-2xl" style={{ color: "var(--app-primary-color, #A70E13)" }} />
                  </div>

                  <p
                    className="mt-5 text-xs font-bold uppercase tracking-[0.2em]"
                    style={{ color: "var(--app-primary-color, #A70E13)" }}
                  >
                    Error 403
                  </p>
                  <h1 className="mt-1 text-2xl font-extrabold text-slate-900">Access Denied</h1>
                  <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                    You don&apos;t have permission to view this workspace. If you believe
                    this is a mistake, please reach out to your administrator.
                  </p>

                  <div className="mt-6 flex items-center justify-center gap-3">
                    <Link
                      to="/"
                      className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white no-underline transition-opacity hover:opacity-90"
                      style={{ background: "var(--app-primary-color, #A70E13)" }}
                    >
                      Go to Home
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {!loading && getClientsData !== undefined && !errorData && !isAccessDenied && (
              <>
                <div className="relative z-10">
                  <UserInfo />
                </div>

                <section className="relative z-10 mt-2 rounded-2xl border border-slate-200/80 bg-white/90 p-4 md:p-6 shadow-sm backdrop-blur">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      {groupid && (
                        <button
                          type="button"
                          title="Go back"
                          onClick={() => navigate(-1)}
                          className="mb-1 inline-flex items-center gap-1 border-0 bg-transparent p-0 text-xs font-semibold"
                          style={{ color: "var(--app-primary-color)" }}
                        >
                          <FaArrowLeft style={{ fontSize: 12 }} />
                          Go Back
                        </button>
                      )}
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Active Workspace</p>
                      <h1 className="text-xl md:text-2xl font-extrabold text-slate-900">
                        Visual Insights for Your Workspace
                      </h1>
                      {reports.length > 0 && (
                        <p className="text-[11px] text-slate-400 mt-0.5">
                           {reports.length} report{reports.length > 1 ? "s" : ""} assigned
                        </p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={toggleDept}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      <FaThLarge className="text-slate-500" />
                      {iconsD ? (
                        <span>
                          Collapse <FaAngleUp className="ml-1 inline text-slate-500" />
                        </span>
                      ) : (
                        <span>
                          Expand <FaAngleDown className="ml-1 inline text-slate-500" />
                        </span>
                      )}
                    </button>
                  </div>

                  <div className={`${iconsD ? "block" : "hidden"}`}>
                    {iconsD && (
                      <div id="Dept_div_id">
                        <LandingKPIReport
                          rlType={getClientsData?.rlType}
                          defaultRL={getClientsData?.defaultRlType}
                          assignedPersonas={assignedPersonas}
                          userGroupSet={userGroupSet}
                          visuals={visuals}
                          groupId={groupid || getClientsData?.userGroupId}
                        />
                      </div>
                    )}
                  </div>

                  {!iconsD && (
                    <p className="text-sm text-slate-500">
                      Please Toggle the Right icons for view all the Dashboard
                    </p>
                  )}
                </section>

                <section className="relative z-10 mt-5 overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-sm backdrop-blur">
                  <div
                    className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-30 blur-2xl"
                    style={{ background: "color-mix(in srgb, var(--app-primary-color, #A70E13) 26%, white)" }}
                    aria-hidden="true"
                  />

                  {/* Persona tabs + view toggle header */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 pt-3">
                    <div className="flex flex-wrap items-center gap-1 overflow-x-auto">
                      {assignedPersonas.length > 0 ? assignedPersonas.map((persona, idx) => (
                        <button
                          key={persona.id}
                          type="button"
                          onClick={() => setTabValue(idx)}
                          className={`whitespace-nowrap rounded-t-lg px-4 py-2 text-xs font-semibold transition-colors border-b-2 ${
                            Tabvalue === idx
                              ? "border-[var(--app-primary-color,#A70E13)] text-[var(--app-primary-color,#A70E13)] bg-white"
                              : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          {persona.name}
                          <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            Tabvalue === idx ? "bg-red-50 text-[var(--app-primary-color,#A70E13)]" : "bg-slate-100 text-slate-500"
                          }`}>
                            {persona.reports?.length ?? 0}
                          </span>
                        </button>
                      )) : (
                        <span className="px-2 py-2 text-[16px] font-bold text-slate-700">{userGroupName} ({reports.length})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pb-1">
                      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                        <button type="button" onClick={() => setReportView("grid")}
                          className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${reportView === "grid" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                          Grid
                        </button>
                        <button type="button" onClick={() => setReportView("table")}
                          className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${reportView === "table" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                          Table
                        </button>
                      </div>
                      <button type="button" onClick={toggleReport}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                        <FaThLarge className="text-slate-500" />
                        {iconsR ? <span>Collapse <FaAngleUp className="ml-1 inline text-slate-500" /></span>
                                : <span>Expand <FaAngleDown className="ml-1 inline text-slate-500" /></span>}
                      </button>
                    </div>
                  </div>

                  {/* Active persona report list */}
                  {(() => {
                    const activePersona = assignedPersonas[Tabvalue];
                    const activeReports = activePersona?.reports || reports;
                    return (
                      <div className={`p-4 md:p-6 ${iconsR ? "block" : "hidden"}`}>
                        {iconsR && reportView === "grid" && (
                          <div id="Report_div_id" className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            {activeReports.map((items, index) => (
                              <DepartmentCard
                                key={index}
                                label={items[1]}
                                sublabel={items[4] || items[7]}
                                icon={<PowerBIIcon size={20} />}
                                onClick={() => navigate(`/reports/${items[0]}/view?groupId=${items[6]}`)}
                                linkTo={`/reports/${items[0]}/view?groupId=${items[6]}`}
                                recentlyUsed={items[12] === true}
                                rls={items[10] === true}
                                stat1Icon={<FaEye style={{ fontSize: 15 }} />}
                                stat1Label={items[13] === 1 ? "View" : "Views"}
                                stat1Value={items[13] ?? 0}
                                stat1Color="products"
                                stat2Icon={<FaClock style={{ fontSize: 15 }} />}
                                stat2Label="Created At"
                                stat2Value={items[9] || "—"}
                                stat2Color="assets"
                              />
                            ))}
                          </div>
                        )}

                        {iconsR && reportView === "table" && (
                          <RankedTable
                            columns={[{ label: "Report" }, { label: "Description" }, { label: "Created At" }, { label: "Your Views", align: "center" }, { label: "RLS", align: "left" }, { label: "Action", align: "left" }]}
                            rows={activeReports}
                            emptyText="No reports available."
                            renderRow={(items) => (
                              <>
                                <td className="px-4 py-3">
                                  <div className="flex flex-col gap-0.5">
                                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                                      {items[1]}
                                      {items[12] === true && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-700 border border-amber-200">
                                          <FaClock size={9} /> Recently Used
                                        </span>
                                      )}
                                    </span>
                                    {items[9] && <span className="text-[11px] text-slate-400">{items[9]}</span>}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-500">
                                  <OverlayTrigger transition={false} overlay={<Tooltip id={`report-desc-${items[0]}`}>{items[4] === "" ? items[1] : items[4]}</Tooltip>}>
                                    <span className="line-clamp-1 inline-block max-w-[520px] align-middle">{items[4] === "" ? "Open interactive dashboard" : items[4]}</span>
                                  </OverlayTrigger>
                                </td>
                                <td className="px-4 py-3 text-xs text-slate-500">
                                  {items[8] || "—"}
                                </td>
                                <td className="px-4 py-3 text-center text-xs text-slate-500">
                                  <span className="inline-flex items-center justify-center gap-1" title="Your views">
                                    <FaEye size={10} /> {items[13] ?? 0}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-left">
                                  {items[10] === true
                                    ? <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700 border border-green-200">RLS</span>
                                    : <span>-</span>}
                                </td>
                                <td className="px-4 py-3 text-left">
                                  <Link to={`/reports/${items[0]}/view?groupId=${items[6]}`}
                                    className="inline-flex rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 no-underline hover:bg-slate-100">
                                    Open
                                  </Link>
                                </td>
                              </>
                            )}
                          />
                        )}
                      </div>
                    );
                  })()}

                  {!iconsR && (
                    <p className="p-4 text-sm text-slate-500">Please Toggle the Right icons for Report to view all Reports</p>
                  )}
                </section>

              </>
            )}

            {!loading && getClientsData === undefined && !errorData && (
              <div className="relative z-10 grid place-items-center text-center">
                <img
                  style={{ width: "400px" }}
                  src={NodataAvailable}
                  alt="Nodata"
                  className="NoDataHome"
                />
                <div style={{ fontSize: "24px" }}>
                  There is no data available
                </div>
              </div>
            )}

            {errorData && (
              <div className="relative z-10 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-700">
                <div>There is Some error, {errorData}</div>
                <div>Plase Try after sometime</div>
              </div>
            )}
          </div>
        )}

        {getClientsData === null && (
          <div className="relative px-4 md:px-8 pb-16" id="main" style={homeMainStyle}>
            <div className="grid place-items-center text-center">
              <img
                style={{ width: "400px" }}
                src={NodataAvailable}
                alt="Nodata"
                className="NoDataHome"
              />
              <div style={{ fontSize: "24px" }}>
                There is no report associated with this persona, Kindly reach
                out to the admin for further details and resolution.
              </div>
            </div>
 
          </div>
        )}
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
    </div>
  );
};

const ReportCardSkeleton = () => (
  <div className="pp-card disabled">
    <div className="pp-card-head">
      <Skeleton variant="rounded" width={40} height={40} style={{ borderRadius: 8 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="40%" height={14} />
      </div>
    </div>
    <div className="pp-card-stats">
      <Skeleton variant="rounded" width="100%" height={48} style={{ borderRadius: 8, flex: 1 }} />
      <Skeleton variant="rounded" width="100%" height={48} style={{ borderRadius: 8, flex: 1 }} />
    </div>
  </div>
);

export default Overview;
